import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import { NewLoadingAndErrors } from '../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { useAuth } from '../../context/auth-context'
import useGetCountriesAndReportGenerationFlags from '../../graphql/countrySettings/hooks/useGetCountriesAndReportGenerationFlags'
import useCountriesHrIsResponsibleFor from '../../graphql/responsible-for-country/hooks/useCountriesHrIsResponsibleFor'
import { useCollapse } from '../../routes/collapse-context'
import { filterCountriesByReportType } from '../../shared/downloadsUtil'
import { RoleName } from '../../types'
import { useReportStore } from './ReportStore'
import { ReportsHeader } from './table/ReportsHeader'
import { ReportsView } from './table/ReportsView'
import { ReportTab } from './types'

export const ReportsContainer: FC = () => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const collapsed = useCollapse()

  const [initStore, storeInitialized] = useReportStore((state) => [state.initStore, state.storeInitialized], shallow)

  const { data, error, loading } = useCountriesHrIsResponsibleFor()

  const {
    data: reportGenerationData,
    loading: reportGenerationLoading,
    error: reportGenerationError,
  } = useGetCountriesAndReportGenerationFlags()

  const userIsDownloader = !!user.roles && user.roles.includes(RoleName.Downloader)

  useEffect(() => {
    if (!!data && !!reportGenerationData && !storeInitialized) {
      const initialActiveTab = ReportTab.MeetingReport
      if (reportGenerationData.getCountriesAndReportGenerationFlags.length > 0) {
        const filteredCountriesData = filterCountriesByReportType(
          initialActiveTab,
          reportGenerationData.getCountriesAndReportGenerationFlags,
        )
        initStore(
          data.findCountriesHrIsResponsibleOf,
          initialActiveTab,
          filteredCountriesData.map((el) => el.country),
        )
      } else {
        initStore(data.findCountriesHrIsResponsibleOf, initialActiveTab, undefined)
      }
    }
  }, [data, initStore, reportGenerationData, storeInitialized])

  const hasToRenderLoadingAndErrors =
    !userIsDownloader ||
    error ||
    reportGenerationError ||
    loading ||
    reportGenerationLoading ||
    !data ||
    !data.findCountriesHrIsResponsibleOf ||
    data.findCountriesHrIsResponsibleOf.length === 0 ||
    !reportGenerationData ||
    !reportGenerationData.getCountriesAndReportGenerationFlags ||
    !storeInitialized

  const hasNoData =
    !data || !data.findCountriesHrIsResponsibleOf || !reportGenerationData || !reportGenerationData.getCountriesAndReportGenerationFlags

  const isPermissionDenied = !error && !userIsDownloader

  const isLoading = loading || reportGenerationLoading || !storeInitialized

  if (hasToRenderLoadingAndErrors) {
    return (
      <ContentContainer collapsed={collapsed}>
        <BackgroundBox r height="fullPage">
          <NewLoadingAndErrors
            isLoading={isLoading}
            hasNoData={hasNoData || isPermissionDenied}
            showSpinner={isLoading}
            error={error || reportGenerationError}
            t={t}
          />
        </BackgroundBox>
      </ContentContainer>
    )
  }
  return (
    <ContentContainer collapsed={collapsed}>
      <ReportsHeader reportGenerationData={reportGenerationData!.getCountriesAndReportGenerationFlags} />
      <ReportsView />
    </ContentContainer>
  )
}
